import React, { forwardRef, useRef, useState } from 'react';
import {
  TextInput,
  Text,
  View,
  StyleSheet,
  TextInputProps,
  ViewStyle,
  Animated,
  Easing,
  NativeSyntheticEvent,
  TextInputFocusEventData,
} from 'react-native';
import { colors, fontSizes, spacing, borderRadius } from '../styles/theme';

interface InputProps extends TextInputProps {
  label?: string;
  error?: string;
  containerStyle?: ViewStyle;
  disabled?: boolean;
}

export const Input = forwardRef<TextInput, InputProps>(
  ({ label, error, containerStyle, disabled, style, onFocus, onBlur, ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    // Animated progress 0 → 1 for focus state. Color interpolation can't
    // use the native driver, so useNativeDriver is false here.
    const focusProgress = useRef(new Animated.Value(0)).current;

    const handleFocus = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
      setFocused(true);
      Animated.timing(focusProgress, {
        toValue: 1,
        duration: 180,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
      onFocus?.(e);
    };

    const handleBlur = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
      setFocused(false);
      Animated.timing(focusProgress, {
        toValue: 0,
        duration: 180,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
      onBlur?.(e);
    };

    const animatedBorderColor = focusProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [colors.border, colors.primary],
    });

    return (
      <View style={[styles.container, containerStyle]}>
        {label && <Text style={styles.label}>{label}</Text>}
        <Animated.View
          style={[
            styles.inputWrapper,
            { borderColor: error ? colors.error : animatedBorderColor },
            disabled && styles.inputDisabled,
          ]}
        >
          <TextInput
            ref={ref}
            style={[styles.input, style]}
            placeholderTextColor={colors.textMuted}
            editable={!disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
        </Animated.View>
        {error && <Text style={styles.errorText}>{error}</Text>}
      </View>
    );
  }
);

Input.displayName = 'Input';

const styles = StyleSheet.create({
  container: {
    marginBottom: spacing.md,
  },
  label: {
    fontSize: fontSizes.sm,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  inputWrapper: {
    borderWidth: 1,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    minHeight: 44,
    justifyContent: 'center',
  },
  input: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: fontSizes.base,
    color: colors.text,
    // react-native-web: remove the browser's default focus-visible outline;
    // the animated wrapper border is our accessibility focus indicator.
    // Cast via `as any` because RN's StyleSheet type doesn't know about
    // web-only properties, but RNW strips them correctly on native.
    ...(({ outlineStyle: 'none', outlineWidth: 0 } as any)),
  },
  inputDisabled: {
    backgroundColor: colors.gray100,
    opacity: 0.6,
  },
  errorText: {
    fontSize: fontSizes.sm,
    color: colors.error,
    marginTop: spacing.xs,
  },
});
